/**
 * Monthly reports (docs/44 §1, deck rulings c136 and c137). Each report is keyed to the grid scan it reads, not to
 * the calendar: its key is the scan day and its period runs from the scan before it (or go-live) to this scan.
 * Four-weekly scans put two scans in one calendar month about once a year, which a month key couldn't hold.
 *
 * "Held" is a band, not zero: the map held when this scan's top-three points sit within the baseline pair's own
 * spread of the baseline average, or within 2 of the 81 points, whichever is larger. One rule for jobs (drafting),
 * the app (reading) and the console, so the label can't drift.
 */
import { z } from 'zod';
import { Day, Month } from './common.ts';

/** A report's key: the scan day for scan-keyed reports (c136), a month for any drafted before that. */
export const ReportKey = z.union([Day, Month]);
export type ReportKey = z.infer<typeof ReportKey>;

export const ReportPeriod = z.object({ from: Day, to: Day, label: z.string().max(60) });
export type ReportPeriod = z.infer<typeof ReportPeriod>;

export const MapMovement = z.enum(['up', 'held', 'down']);
export type MapMovement = z.infer<typeof MapMovement>;

/** The smallest "held" band, in top-three points of 81 (c137). */
export const HELD_MIN_POINTS = 2;

/** The held band for a baseline pair: its spread, or 2 points, whichever is larger. */
export function heldBand(baseline: readonly number[]): number {
  if (baseline.length < 2) return HELD_MIN_POINTS;
  return Math.max(Math.max(...baseline) - Math.min(...baseline), HELD_MIN_POINTS);
}

/** Up, held or down against the baseline average; null with no baseline to compare with. */
export function mapMovement(now: number, baseline: readonly number[]): MapMovement | null {
  if (!baseline.length || !Number.isFinite(now)) return null;
  const avg = baseline.reduce((s, v) => s + v, 0) / baseline.length;
  const d = now - avg;
  if (Math.abs(d) <= heldBand(baseline)) return 'held';
  return d > 0 ? 'up' : 'down';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Sep 3 to Oct 1, 2026", or "Dec 5, 2026 to Jan 2, 2027" across a year end. Days are YYYY-MM-DD. */
export function periodLabel(from: string, to: string): string {
  const f = Day.parse(from);
  const t = Day.parse(to);
  const part = (d: string) => `${MONTHS[Number(d.slice(5, 7)) - 1]} ${Number(d.slice(8, 10))}`;
  return f.slice(0, 4) === t.slice(0, 4) ? `${part(f)} to ${part(t)}, ${t.slice(0, 4)}` : `${part(f)}, ${f.slice(0, 4)} to ${part(t)}, ${t.slice(0, 4)}`;
}

/** The whole period for a report keyed to `scanDay`, from the scan (or go-live day) before it. */
export function reportPeriod(previousDay: string, scanDay: string): ReportPeriod {
  return { from: previousDay, to: scanDay, label: periodLabel(previousDay, scanDay) };
}
